import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Flame, Apple, Moon, Sparkles, CheckCircle2 } from 'lucide-react';
const modules = [{
  phase: 'Fase 01',
  range: 'Módulo 1-3',
  title: 'Entendiendo el Cortisol y la Grasa Abdominal',
  desc: 'Descubre por qué tu cuerpo almacena grasa de forma diferente ahora y cómo apagar la señal de estrés constante.',
  highlights: ['Por qué aparece la **Barriga Hormonal** después de los 40', 'Cómo regular el **Cortisol** sin medicamentos', 'Identifica los **disparadores ocultos** del estrés'],
  icon: <Flame size={32} className="text-sage-700" data-id="element-246" />
}, {
  phase: 'Fase 02',
  range: 'Módulo 4-6',
  title: 'Nutrición Metabólica y Descanso Digestivo',
  desc: 'Aprende a comer para tus hormonas actuales sin pasar hambre.',
  highlights: ['Alimentos que reactivan tu **Metabolismo**', 'Estrategias para **Desinflamar** tu abdomen', 'Recupera tu **Energía** desde el primer día'],
  icon: <Apple size={32} className="text-sage-700" data-id="element-247" />
}, {
  phase: 'Fase 03',
  range: 'Módulo 7-9',
  title: 'El Manual del Sueño y Suplementación Global',
  desc: 'Protocolos exactos para volver a dormir de corrido y los suplementos clave que realmente marcan la diferencia.',
  highlights: ['Calma los **Sofocos** nocturnos de forma natural', 'Recupera el **Sueño profundo** y reparador', 'Los **3 suplementos** que sí funcionan'],
  icon: <Moon size={32} className="text-sage-700" data-id="element-248" />
}, {
  phase: 'Fase 04',
  range: 'Módulo 10-15',
  title: 'Claridad Mental, Gestión Emocional y Plan de Acción',
  desc: 'Despídete de la neblina mental con tu plan paso a paso de 21 días.',
  highlights: ['Adiós a la **Neblina Mental** y el cansancio', 'Equilibra tus **Emociones** y reduce la irritabilidad', 'Tu plan de acción de **21 días** listo para usar'],
  icon: <Sparkles size={32} className="text-sage-700" data-id="element-249" />
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
// Renders text with **bold** markdown-style emphasis
function renderHighlight(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-sage-800" data-id="element-250">
          {part.slice(2, -2)}
        </strong>;
    }
    return <span key={i} data-id="element-251">{part}</span>;
  });
}
export function ModulesSection() {
  return <section className="py-20 md:py-32 bg-cream-dark relative overflow-hidden" data-id="element-252">
      {/* Decorative background */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-sage-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none" data-id="element-253"></div>
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-sage-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none" data-id="element-254"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-255">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-256">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-block py-1 px-3 rounded-full bg-sage-100 text-sage-800 text-xs font-semibold tracking-widest uppercase mb-6" data-id="element-257">
            Lo Que Obtendrás
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" data-id="element-258">
            Tu ruta clara hacia el bienestar
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-charcoal-light" data-id="element-259">
            15 módulos estructurados en 4 fases progresivas — diseñados para
            resultados reales, paso a paso.
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-260">
          {modules.map((mod, index) => <motion.div key={index} variants={itemVariants} whileHover={{
          y: -4
        }} className="bg-cream rounded-3xl p-8 md:p-10 shadow-sm border border-sage-100 hover:shadow-lg hover:border-sage-300 transition-all relative overflow-hidden group" data-id="element-261">
              {/* Decorative phase number watermark */}
              <span className="absolute -top-6 -right-2 text-[8rem] font-serif font-bold text-sage-100 select-none pointer-events-none leading-none" aria-hidden="true" data-id="element-262">
                0{index + 1}
              </span>

              {/* Header: Phase + Range */}
              <div className="flex items-center justify-between mb-6 relative z-10" data-id="element-263">
                <span className="text-sage-700 font-semibold text-xs tracking-widest uppercase" data-id="element-264">
                  {mod.phase}
                </span>
                <span className="bg-sage-100 text-sage-800 text-xs font-semibold px-3 py-1 rounded-full tracking-wide" data-id="element-265">
                  {mod.range}
                </span>
              </div>

              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-sage-100 to-cream-dark rounded-2xl flex items-center justify-center mb-6 relative z-10" data-id="element-266">
                {mod.icon}
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold mb-4 leading-tight relative z-10" data-id="element-267">
                {mod.title}
              </h3>

              {/* Description */}
              <p className="text-charcoal-light leading-relaxed mb-6 relative z-10" data-id="element-268">
                {mod.desc}
              </p>

              {/* Highlights list */}
              <ul className="space-y-3 relative z-10 pt-6 border-t border-sage-100" data-id="element-269">
                {mod.highlights.map((hl, i) => <li key={i} className="flex items-start gap-3" data-id="element-270">
                    <CheckCircle2 size={18} className="text-sage-600 flex-shrink-0 mt-0.5" data-id="element-271" />
                    <span className="text-sm text-charcoal-light leading-relaxed" data-id="element-272">
                      {renderHighlight(hl)}
                    </span>
                  </li>)}
              </ul>
            </motion.div>)}
        </motion.div>

        {/* Trust footer */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mt-16 max-w-2xl mx-auto" data-id="element-273">
          <p className="text-charcoal-light italic font-serif text-xl md:text-2xl leading-relaxed" data-id="element-274">
            "Cada módulo te acerca un paso más a sentirte ligera, clara y en
            control de tu cuerpo."
          </p>
        </motion.div>
      </div>
    </section>;
}